import { updateAppConfig } from '../api/appConfig';

// Route table mirrors src/App.js exactly. Keep these in sync if routes change.
export const ROUTES = {
  dashboard: '/dashboard',
  users: '/users',
  drivers: '/drivers',
  rides: '/rides',
  payments: '/payments',
  promo_codes: '/promo-codes',
  fare_config: '/fare-config',
  support: '/support',
  offers: '/offers',
  document_types: '/document-types',
  notifications: '/notifications',
  settings: '/settings',
};

const PAGE_LABELS = {
  dashboard: 'Dashboard',
  users: 'Users',
  drivers: 'Drivers',
  rides: 'Rides',
  payments: 'Payments',
  promo_codes: 'Promo Codes',
  fare_config: 'Fare Configuration',
  support: 'Support Tickets',
  offers: 'Offers',
  document_types: 'Document Types',
  notifications: 'Notifications',
  settings: 'Settings',
};

const APP_NAMES = ['customer', 'driver'];

// Only pages with a real free-text search box get a pre-filled query today.
const SEARCHABLE_ENTITIES = ['users'];
// Only pages with a real "create" modal get the auto-open behavior.
const CREATABLE_ENTITIES = ['promo_codes', 'fare_config', 'document_types'];

export const systemPrompt = `You are the in-app assistant for Vandigo Admin, a ride-hailing platform's admin panel.

You can answer questions about how the panel works (informational) and you can also take actions in the app on the admin's behalf by calling one of the provided functions (actions).

STRICT RULE: only call a function when the admin clearly wants you to *do* something right now (navigate somewhere, switch the theme, look up a specific record, open a create form, enable/disable maintenance mode or force update for an app, open the app config screen). For anything informational or explanatory ("what does X page do", "how do I do Y", "explain Z") — just answer in plain conversational text. Never call a function to answer a question that doesn't require action. Never invent a function call just to seem helpful.

The customer app and driver app each have independent maintenance-mode and force-update settings, controllable via set_maintenance_mode / set_force_update — always confirm which app ("customer" or "driver") the admin means before calling these if it isn't clear from their message. The App Config screen itself (where an admin can review/edit both apps' settings visually) has no sidebar entry by design — it's reachable only by asking you, via open_app_config, the same way the server logs viewer works.

The admin panel sections are: ${Object.entries(PAGE_LABELS).map(([k, v]) => `${k} (${v})`).join(', ')}.

Keep replies short and conversational, matching the tone of a helpful internal tool, not a customer-facing chatbot. After a function executes, confirm briefly in natural language what you did.`;

export const tools = [
  {
    name: 'navigate_to_page',
    description: 'Navigate the admin panel to a specific section/page.',
    parameters: {
      type: 'OBJECT',
      properties: {
        page: { type: 'STRING', enum: Object.keys(ROUTES), description: 'The page to navigate to.' },
      },
      required: ['page'],
    },
  },
  {
    name: 'toggle_theme',
    description: 'Switch the admin panel between light and dark mode.',
    parameters: { type: 'OBJECT', properties: {} },
  },
  {
    name: 'search_record',
    description: 'Search/filter a list page for a specific record by name or keyword. Currently only supported for Users.',
    parameters: {
      type: 'OBJECT',
      properties: {
        entity: { type: 'STRING', enum: SEARCHABLE_ENTITIES, description: 'Which list to search.' },
        query: { type: 'STRING', description: 'The search text (e.g. a name, email, or phone number).' },
      },
      required: ['entity', 'query'],
    },
  },
  {
    name: 'open_create_modal',
    description: 'Open the "create new" form for an entity that supports creating new records.',
    parameters: {
      type: 'OBJECT',
      properties: {
        entity: { type: 'STRING', enum: CREATABLE_ENTITIES, description: 'Which entity to create a new record for.' },
      },
      required: ['entity'],
    },
  },
  {
    name: 'open_server_logs',
    description: 'Open the server logs viewer (e.g. when the admin asks to see server logs, backend logs, error logs, or debug an issue on the server). Requires a separate logs password the admin will be prompted for — never ask the admin for that password in chat.',
    parameters: { type: 'OBJECT', properties: {} },
  },
  {
    name: 'open_app_config',
    description: 'Open the App Config screen, where maintenance mode and force-update settings for the customer app and driver app can be reviewed/edited visually. Use this when the admin wants to see or edit these settings in the UI rather than just toggling them directly via set_maintenance_mode/set_force_update.',
    parameters: { type: 'OBJECT', properties: {} },
  },
  {
    name: 'set_maintenance_mode',
    description: 'Enable or disable maintenance mode for the customer app or the driver app. When enabled, that app blocks users at launch with a maintenance screen until disabled.',
    parameters: {
      type: 'OBJECT',
      properties: {
        app: { type: 'STRING', enum: APP_NAMES, description: 'Which app to control.' },
        enabled: { type: 'BOOLEAN', description: 'true to turn maintenance mode on, false to turn it off.' },
        title: { type: 'STRING', description: 'Optional dialog title shown to users.' },
        message: { type: 'STRING', description: 'Optional dialog message shown to users.' },
      },
      required: ['app', 'enabled'],
    },
  },
  {
    name: 'set_force_update',
    description: "Enable or disable a forced update for the customer app or the driver app. When enabled with a minimum build number, users on an older build are blocked at launch until they update.",
    parameters: {
      type: 'OBJECT',
      properties: {
        app: { type: 'STRING', enum: APP_NAMES, description: 'Which app to control.' },
        enabled: { type: 'BOOLEAN', description: 'true to require the update, false to turn it off.' },
        min_build_android: { type: 'NUMBER', description: 'Minimum Android build number required.' },
        min_build_ios: { type: 'NUMBER', description: 'Minimum iOS build number required.' },
        url_android: { type: 'STRING', description: 'Play Store URL to send Android users to.' },
        url_ios: { type: 'STRING', description: 'App Store URL to send iOS users to.' },
        title: { type: 'STRING', description: 'Optional dialog title shown to users.' },
        message: { type: 'STRING', description: 'Optional dialog message shown to users.' },
      },
      required: ['app', 'enabled'],
    },
  },
];

/**
 * Maps a function name + args to real app behavior. `ctx` is supplied by the
 * chat widget and carries the actual navigate/theme/toast hooks so this file
 * stays decoupled from any specific UI component. Async because a couple of
 * actions (set_maintenance_mode, set_force_update) call the backend API.
 */
export async function dispatchAction(name, args, ctx) {
  switch (name) {
    case 'navigate_to_page': {
      const path = ROUTES[args.page];
      if (!path) return { status: 'error', detail: `Unknown page "${args.page}".` };
      ctx.navigate(path);
      return { status: 'executed', detail: `Navigated to ${PAGE_LABELS[args.page]}.` };
    }
    case 'toggle_theme': {
      ctx.toggleTheme();
      return { status: 'executed', detail: 'Theme toggled.' };
    }
    case 'search_record': {
      const path = ROUTES[args.entity];
      if (!path || !SEARCHABLE_ENTITIES.includes(args.entity)) {
        return { status: 'error', detail: `Search isn't available for "${args.entity}" yet.` };
      }
      ctx.navigate(`${path}?q=${encodeURIComponent(args.query ?? '')}`);
      return { status: 'executed', detail: `Opened ${PAGE_LABELS[args.entity]} filtered by "${args.query}".` };
    }
    case 'open_create_modal': {
      const path = ROUTES[args.entity];
      if (!path || !CREATABLE_ENTITIES.includes(args.entity)) {
        return { status: 'error', detail: `There's no create form for "${args.entity}".` };
      }
      ctx.navigate(`${path}?action=create`);
      return { status: 'executed', detail: `Opened the create form for ${PAGE_LABELS[args.entity]}.` };
    }
    case 'open_server_logs': {
      ctx.openLogs();
      return { status: 'executed', detail: "Opened the server logs viewer — I've prompted for the logs password." };
    }
    case 'open_app_config': {
      ctx.openAppConfig();
      return { status: 'executed', detail: 'Opened the App Config screen.' };
    }
    case 'set_maintenance_mode': {
      if (!APP_NAMES.includes(args.app)) {
        return { status: 'error', detail: `Unknown app "${args.app}".` };
      }
      try {
        await updateAppConfig(args.app, {
          maintenance_enabled: !!args.enabled,
          ...(args.title ? { maintenance_title: args.title } : {}),
          ...(args.message ? { maintenance_message: args.message } : {}),
        });
        ctx.invalidateAppConfig?.();
        return {
          status: 'executed',
          detail: `Maintenance mode ${args.enabled ? 'enabled' : 'disabled'} for the ${args.app} app.`,
        };
      } catch (e) {
        return { status: 'error', detail: `Failed to update maintenance mode: ${e.message}` };
      }
    }
    case 'set_force_update': {
      if (!APP_NAMES.includes(args.app)) {
        return { status: 'error', detail: `Unknown app "${args.app}".` };
      }
      try {
        await updateAppConfig(args.app, {
          force_update_enabled: !!args.enabled,
          ...(args.min_build_android != null ? { force_update_min_build_android: Number(args.min_build_android) } : {}),
          ...(args.min_build_ios != null ? { force_update_min_build_ios: Number(args.min_build_ios) } : {}),
          ...(args.url_android ? { force_update_url_android: args.url_android } : {}),
          ...(args.url_ios ? { force_update_url_ios: args.url_ios } : {}),
          ...(args.title ? { force_update_title: args.title } : {}),
          ...(args.message ? { force_update_message: args.message } : {}),
        });
        ctx.invalidateAppConfig?.();
        return {
          status: 'executed',
          detail: `Force update ${args.enabled ? 'enabled' : 'disabled'} for the ${args.app} app.`,
        };
      } catch (e) {
        return { status: 'error', detail: `Failed to update force update: ${e.message}` };
      }
    }
    default:
      return { status: 'error', detail: `Unknown action "${name}".` };
  }
}
